import { useMemo } from "react";

import { bool } from "prop-types";
import styled from "styled-components/macro";

import { useGetPermissions } from "Flocknote/API/queries";
import { usePermissions } from "Flocknote/apiHooks";

import {
  supersearchWidth,
  toolbarButtonSize,
  toolbarPadding,
} from "NavBar/styleConstants";
import { AnimatePresence, motion } from "motion/react";

import FnrOpacityToggle from "UI/FnrOpacityToggle";
import { marketingColors } from "UI/colors";

import { addOpacity } from "Utilities/Colors/colorUtils";
import useHasFinePointer from "Utilities/Hooks/useHasFinePointer";
import { defaultShadow } from "Utilities/interpolations/shadow";

import AddMember from "./AddMember";
import HoverText from "./HoverText";
import SendEmail from "./SendEmail";
import SendText from "./SendText";
import SuperSearch from "./SuperSearch";
import { useSuperSearchState } from "./SuperSearch/superSearchStore";

const mostlyBlack = addOpacity("#000000", 0.5);
const transparentBlack = addOpacity("#000000", 0);
const stagger = 0.05;
const duration = 0.3;

const Toolbar = ({ isNavToolbar = false }) => {
  const { isInitialLoading } = useGetPermissions();
  const [{ canSendNotes, canAddMember, canSuperSearch }] = usePermissions();
  const superSearchState = useSuperSearchState(isNavToolbar);
  const { isActive: searchIsActive } = superSearchState;
  const hasFinePointer = useHasFinePointer();

  const numberOfButtons = useMemo(() => {
    let theNumber = 0;
    if (canSuperSearch) theNumber += 1;
    if (canSendNotes) theNumber += 2;
    if (canAddMember) theNumber += 1;
    return theNumber;
  }, [canAddMember, canSendNotes, canSuperSearch]);

  const wrapperAnimate = useMemo(() => {
    const backgroundColor = isInitialLoading ? transparentBlack : mostlyBlack;
    const width = searchIsActive
      ? supersearchWidth + toolbarPadding * 2
      : toolbarButtonSize * numberOfButtons +
        toolbarPadding * (numberOfButtons - 1);
    return { backgroundColor, width, transition: 0.4 };
  }, [isInitialLoading, numberOfButtons, searchIsActive]);

  const toolbarButtonAnimate = useMemo(
    () =>
      searchIsActive
        ? { width: 0, scale: 0 }
        : { width: toolbarButtonSize, scale: 1 },
    [searchIsActive]
  );

  if (!canSendNotes && !canAddMember && !canSuperSearch) return null;

  return (
    <CenteringDiv show={!isInitialLoading} scale delayIn={0.3}>
      <Wrapper initial={false} animate={wrapperAnimate}>
        <AnimatePresence>
          {canSendNotes && (
            <ToolbarButtonMotion
              key="toobarSendEmail"
              initial={false}
              animate={{
                ...toolbarButtonAnimate,
                marginRight: searchIsActive ? 0 : toolbarPadding / 2,
              }}
              transition={{
                duration,
                delay: searchIsActive ? 0 : stagger * 2,
              }}
            >
              <SendEmail />
            </ToolbarButtonMotion>
          )}
          {canSendNotes && (
            <ToolbarButtonMotion
              key="toolbarSendText"
              initial={false}
              animate={{
                ...toolbarButtonAnimate,
                marginLeft: searchIsActive ? 0 : toolbarPadding / 2,
                marginRight: searchIsActive
                  ? 0
                  : toolbarPadding / (canAddMember ? 2 : 1),
              }}
              transition={{ duration, delay: stagger }}
            >
              <SendText />
            </ToolbarButtonMotion>
          )}
          {canAddMember && (
            <ToolbarButtonMotion
              key="toolbarAddMember"
              initial={false}
              animate={{
                ...toolbarButtonAnimate,
                marginLeft:
                  searchIsActive || !canSendNotes ? 0 : toolbarPadding / 2,
                marginRight: searchIsActive ? 0 : toolbarPadding,
              }}
              transition={{
                duration,
                delay: searchIsActive ? stagger * 2 : 0,
              }}
            >
              <AddMember isNavToolbar={isNavToolbar} />
            </ToolbarButtonMotion>
          )}
          {canSuperSearch && <SuperSearch {...superSearchState} />}
        </AnimatePresence>
      </Wrapper>
      {hasFinePointer && <HoverText />}
    </CenteringDiv>
  );
};

Toolbar.propTypes = { isNavToolbar: bool };

const ToolbarButtonMotion = styled(motion.div)`
  display: flex;
  align-items: center;
  transform-origin: center;
`;

const Wrapper = styled(motion.div)`
  display: flex;
  border-radius: ${toolbarPadding + toolbarButtonSize}px;
  padding: ${toolbarPadding}px;
  height: ${toolbarButtonSize}px;
  ${defaultShadow}
  border: 1px solid ${addOpacity(marketingColors.gray, 0.15)};
`;

const CenteringDiv = styled(FnrOpacityToggle)`
  width: 100%;
  display: flex;
  align-items: center;
  padding: 8px 0px 0px 0px;
  flex-direction: column;
`;

export default Toolbar;
